import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  getChatNotificationsApi,
  markChatAsReadApi,
  markChatSessionAsReadApi,
  markInternalChatAsReadApi,
} from "../services/chat.services";
import { getCookie } from "../helpers/localStorage";
import {
  ACCESS_TOKEN,
  FacilityID,
  ORGANISATONID,
} from "../constants/defaultKeys";

// Close codes sent by the server when the token is invalid/expired (4401) or the
// user has no access (4403). Reconnecting will never succeed for these.
const FATAL_CLOSE_CODES = [4401, 4403];
const MAX_RECONNECT_DELAY_MS = 30000;
const TOAST_DURATION_MS = 5000;

export const getNotificationKey = (item) =>
  item?.session_id ||
  `${item?.patient_id}_${item?.chat_type}_${item?.ip_id}`;

export const useChatNotifications = () => {
  const token = getCookie(ACCESS_TOKEN);
  const organizationId = getCookie(ORGANISATONID);
  const facility = getCookie(FacilityID);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);

  const ws = useRef(null);
  const toastTimeoutRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!facility) return;
    try {
      setLoading(true);
      const response = await getChatNotificationsApi(facility);
      setNotifications(response.data?.data || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching chat notifications:", err);
      setError("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, [facility]);

  const dismissNotification = useCallback((item) => {
    const key = getNotificationKey(item);
    setNotifications((prev) =>
      prev.filter((entry) => getNotificationKey(entry) !== key)
    );
  }, []);

  const markNotificationAsRead = useCallback(async (item) => {
    // session_id is the full session id ("IP12_partner" / "IP12_internal");
    // pass it through unchanged.
    if (item?.session_id) {
      await markChatSessionAsReadApi(item.session_id);
      return;
    }
    if (!item?.ip_id) return;
    const markRead =
      item?.chat_type === "internal"
        ? markInternalChatAsReadApi
        : markChatAsReadApi;
    await markRead(item.ip_id);
  }, []);

  const handleNotificationClick = useCallback(
    (item) => {
      dismissNotification(item);
      markNotificationAsRead(item).catch((err) => {
        console.error("Failed to mark notification as read:", err);
        fetchNotifications();
      });
    },
    [dismissNotification, markNotificationAsRead, fetchNotifications]
  );

  const unreadBadgeCount = useMemo(
    () =>
      notifications.reduce((sum, item) => {
        const count = Number(item?.unread_count);
        if (Number.isFinite(count) && count > 0) return sum + count;
        return sum + 1;
      }, 0),
    [notifications]
  );

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!token || !facility) return undefined;

    let disposed = false;
    let attempt = 0;
    let reconnectTimer = null;

    const connect = () => {
      if (disposed) return;
      const wsUrl = `${
        process.env.REACT_APP_WS_BASE_URL
      }/ws/notifications/?token=${encodeURIComponent(
        token
      )}&organization=${encodeURIComponent(
        organizationId ?? ""
      )}&facility=${encodeURIComponent(facility)}`;
      const socket = new WebSocket(wsUrl);
      ws.current = socket;

      socket.onopen = () => {
        attempt = 0;
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.notification) {
            setNotification(data);
            fetchNotifications();
            if (toastTimeoutRef.current) {
              clearTimeout(toastTimeoutRef.current);
            }
            toastTimeoutRef.current = setTimeout(() => {
              toastTimeoutRef.current = null;
              setNotification(null);
            }, TOAST_DURATION_MS);
          }
        } catch (err) {
          console.error("Error parsing WebSocket message:", err);
        }
      };

      socket.onerror = (event) => {
        console.log("Notifications WebSocket error:", event);
      };

      socket.onclose = (event) => {
        if (ws.current === socket) ws.current = null;
        if (disposed || FATAL_CLOSE_CODES.includes(event?.code)) return;
        const delay = Math.min(1000 * 2 ** attempt, MAX_RECONNECT_DELAY_MS);
        attempt += 1;
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      disposed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws.current) {
        ws.current.close();
        ws.current = null;
      }
    };
  }, [fetchNotifications, token, organizationId, facility]);

  useEffect(
    () => () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    },
    []
  );

  return {
    notifications,
    loading,
    error,
    notification,
    unreadBadgeCount,
    refetch: fetchNotifications,
    dismissNotification,
    handleNotificationClick,
  };
};
